import { useEffect, useState } from 'react';

const ChartLegend = ({
  chartRef,
  showBreakdown,
  chartType,
  areaChartStacked,
  colors
}) => {
  // Local state to handle both visibility logic and UI updates
  const [checkboxStates, setCheckboxStates] = useState({
    total: true,
    successfulDiets: true,
    dietsWithWarning: true,
    noDiets: true,
    average: true
  });


  // Reset visibility when switching between breakdown modes or chart types
  useEffect(() => {
    const initialState = {
      total: true,
      successfulDiets: true,
      dietsWithWarning: true,
      noDiets: true,
      average: true
    };

    setCheckboxStates(initialState);
  }, [showBreakdown, chartType]);

  // Toggle dataset visibility by directly manipulating chart data
  const toggleDataset = (datasetKey) => {
    const chart = chartRef.current;
    if (!chartRef.current) return;

    // Capture current Y-axis range and lock it to preserve scale
    const yScale = chart.scales?.y;
    const currentMin = yScale?.min;
    const currentMax = yScale?.max;

    // Toggle visibility state
    const isVisible = !checkboxStates[datasetKey];

    // Apply visibility changes to chart data
    const datasetLabel = getDatasetLabel(datasetKey);
    const datasetIndex = chart.data.datasets.findIndex(ds => ds.label === datasetLabel);

    if (isVisible) {
      // Unhide dataset
      chart.data.datasets[datasetIndex].hidden = false;
    } else if (datasetIndex !== -1) {
      // Lock scale in options
      // ensure they are numbers or undefined
      chart.options.scales.y.min = (typeof currentMin === 'number') ? currentMin : undefined;
      chart.options.scales.y.max = (typeof currentMax === 'number') ? currentMax : undefined;

      // Hide dataset
      chart.data.datasets[datasetIndex].hidden = true;
    }

    // Update chart without animation
    chart.update('none');

      // Update checkbox state
    setCheckboxStates(prev => ({
      ...prev,
      [datasetKey]: isVisible
    }));
  };

  // Helper function to map datasetKey to chart dataset label
  const getDatasetLabel = (datasetKey) => {
    const labelMap = {
      total: 'Total',
      successfulDiets: 'Successful Diets',
      dietsWithWarning: 'Diets With Warning',
      noDiets: 'No Diets',
      average: 'Average'
    };
    return labelMap[datasetKey];
  };

  return (
    <div className="flex flex-wrap justify-center sm:justify-end mt-4 gap-3 sm:gap-4">
      {showBreakdown ? (
        <div className="flex flex-wrap justify-center sm:justify-end gap-3 sm:gap-4 p-2 rounded-lg" data-intro="chart-legend">
          {/* Show Total first, but hide it for area stacked mode, bar charts, and doughnut charts */}
          {!(chartType === 'area' && areaChartStacked) && chartType !== 'bar' && chartType !== 'doughnut' && (
            <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
              <div
                className="relative w-4 h-4 bg-blue-400 dark:bg-blue-600 border border-blue-500 dark:border-blue-700 rounded-sm flex items-center justify-center transition-all"
                onClick={() => toggleDataset('total')}
              >
                {checkboxStates.total && (
                  <span className="text-white text-xs font-bold leading-none m-0 p-0" style={{fontSize: '12px', lineHeight: '1'}}>✓</span>
                )}
              </div>
              <span
                className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
                onClick={() => toggleDataset('total')}
              >
                Total
              </span>
            </label>
          )}
          <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
            <div
              className="relative w-4 h-4 bg-emerald-400 dark:bg-emerald-600 border border-emerald-500 dark:border-emerald-700 rounded-sm flex items-center justify-center transition-all"
              onClick={() => toggleDataset('successfulDiets')}
            >
              {checkboxStates.successfulDiets && (
                <span className="text-white text-xs font-bold leading-none m-0 p-0" style={{fontSize: '12px', lineHeight: '1'}}>✓</span>
              )}
            </div>
            <span
              className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => toggleDataset('successfulDiets')}
            >
              Successful Diets
            </span>
          </label>
          <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
            <div
              className="relative w-4 h-4 bg-amber-400 dark:bg-amber-600 border border-amber-500 dark:border-amber-700 rounded-sm flex items-center justify-center transition-all"
              onClick={() => toggleDataset('dietsWithWarning')}
            >
              {checkboxStates.dietsWithWarning && (
                <span className="text-white text-xs font-bold leading-none m-0 p-0" style={{fontSize: '12px', lineHeight: '1'}}>✓</span>
              )}
            </div>
            <span
              className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => toggleDataset('dietsWithWarning')}
            >
              Diets With Warning
            </span>
          </label>
          <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
            <div
              className="relative w-4 h-4 bg-red-400 dark:bg-red-600 border border-red-500 dark:border-red-700 rounded-sm flex items-center justify-center transition-all"
              onClick={() => toggleDataset('noDiets')}
            >
              {checkboxStates.noDiets && (
                <span className="text-white text-xs font-bold leading-none m-0 p-0" style={{fontSize: '12px', lineHeight: '1'}}>✓</span>
              )}
            </div>
            <span
              className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => toggleDataset('noDiets')}
            >
              No Diets
            </span>
          </label>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center sm:justify-end gap-3 sm:gap-4 p-2 rounded-lg" data-intro="chart-legend">
          <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
            <div
              className="relative w-4 h-4 bg-blue-400 dark:bg-blue-600 border border-blue-500 dark:border-blue-700 rounded-sm flex items-center justify-center transition-all"
              onClick={() => toggleDataset('total')}
            >
              {checkboxStates.total && (
                <span className="text-white text-xs font-bold leading-none m-0 p-0" style={{fontSize: '12px', lineHeight: '1'}}>✓</span>
              )}
            </div>
            <span
              className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => toggleDataset('total')}
            >
              Total
            </span>
          </label>
          {chartType !== 'bar' && (
            <label className="flex items-center space-x-1.5 cursor-pointer hover:opacity-80 transition-opacity">
              <div
                className="relative w-4 h-4 border-2 border-dashed border-purple-400 dark:border-purple-600 rounded-sm flex items-center justify-center transition-all bg-transparent"
                onClick={() => toggleDataset('average')}
                style={{borderColor: colors.average}}
              >
                {checkboxStates.average && (
                  <span className="text-purple-600 dark:text-purple-400 text-xs font-bold leading-none m-0 p-0" style={{color: colors.average, fontSize: '12px', lineHeight: '1'}}>✓</span>
                )}
              </div>
              <span
                className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
                onClick={() => toggleDataset('average')}
              >
                Average
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartLegend;
